<script setup lang="ts">
import type { Toast, ToastAction } from '@/types'

// ============================================
// Props
// ============================================

defineProps<{
  toast: Toast
}>()

const emit = defineEmits<{
  close: [id: string]
}>()

// ============================================
// Methods
// ============================================

function getIconPath(type: string): string {
  switch (type) {
    case 'success':
      return 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    case 'error':
      return 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z'
    case 'warning':
      return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
    case 'info':
    default:
      return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
  }
}

function getColorClasses(type: string): { bg: string; icon: string; border: string } {
  switch (type) {
    case 'success':
      return {
        bg: 'bg-green-50',
        icon: 'text-green-500',
        border: 'border-green-200'
      }
    case 'error':
      return {
        bg: 'bg-red-50',
        icon: 'text-red-500',
        border: 'border-red-200'
      }
    case 'warning':
      return {
        bg: 'bg-yellow-50',
        icon: 'text-yellow-500',
        border: 'border-yellow-200'
      }
    case 'info':
    default:
      return {
        bg: 'bg-blue-50',
        icon: 'text-blue-500',
        border: 'border-blue-200'
      }
  }
}

function getActionClasses(action: ToastAction): string {
  const baseClasses = 'px-3 py-1.5 text-sm font-medium rounded-md transition-colors'
  if (action.variant === 'primary') {
    return `${baseClasses} bg-vfr-red text-white hover:bg-vfr-red-dark`
  }
  return `${baseClasses} bg-gray-200 text-gray-700 hover:bg-gray-300`
}
</script>

<template>
  <div
    class="toast flex flex-col gap-2 p-4 rounded-lg shadow-lg border min-w-[300px] max-w-md"
    :class="[getColorClasses(toast.type).bg, getColorClasses(toast.type).border]"
    role="alert"
  >
    <!-- Header Row -->
    <div class="flex items-start gap-3">
      <!-- Icon -->
      <svg
        class="w-6 h-6 flex-shrink-0"
        :class="getColorClasses(toast.type).icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          :d="getIconPath(toast.type)"
        />
      </svg>

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <!-- Title (optional) -->
        <p
          v-if="toast.title"
          data-testid="toast-title"
          class="text-sm font-semibold text-gray-900"
        >
          {{ toast.title }}
        </p>

        <!-- Message -->
        <p class="text-sm text-gray-700" :class="{ 'mt-1': toast.title }">
          {{ toast.message }}
        </p>
      </div>

      <!-- Close button -->
      <button
        type="button"
        class="flex-shrink-0 p-1 rounded-full hover:bg-gray-200 transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
        aria-label="Fermer"
        @click="emit('close', toast.id)"
      >
        <svg
          class="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Actions (optional) -->
    <div
      v-if="toast.actions && toast.actions.length > 0"
      data-testid="toast-actions"
      class="flex gap-2 ml-9"
    >
      <button
        v-for="(action, index) in toast.actions"
        :key="index"
        :data-testid="`toast-action-${index}`"
        type="button"
        :class="getActionClasses(action)"
        @click="action.handler"
      >
        {{ action.label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.toast {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
